import Localization from "sap/base/i18n/Localization";

/**
 * @namespace com.ang.portal.service
 */

/**
 * The portal's UI language, persisted per browser and per user account.
 *
 * UI5 resolves the language once at bootstrap, and much of this app builds
 * texts imperatively (`bundle.getText(...)`, cached dialogs, plugin components
 * loaded on demand) rather than through `{i18n>...}` bindings. Those texts do
 * not re-render on a live language change, so switching the language persists
 * the choice and reloads the page — the shell, every dialog and every plugin
 * then come up in one consistent language.
 *
 * The chosen language is written to `localStorage` (so it survives logout and
 * applies to the login page) and to the user's account (so it follows them to
 * another browser). On login the account value wins — see `syncFromUser`.
 */

/** Languages the portal ships translations for. Keep in sync with the backend's languages.ts. */
export const SUPPORTED_LANGUAGES = ["de", "en"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Portal default, also the `users.language` column default. */
export const DEFAULT_LANGUAGE: SupportedLanguage = "de";

/**
 * Must match the key read by the pre-bootstrap snippet in index.html — that
 * snippet applies the language before UI5 loads, which is what makes the
 * initial render come up translated.
 */
const STORAGE_KEY = "portal.language";

const isSupported = (value: unknown): value is SupportedLanguage =>
	typeof value === "string" &&
	(SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/** Read the persisted choice. Private browsing modes can throw on access. */
const readStored = (): SupportedLanguage | null => {
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY);
		return isSupported(stored) ? stored : null;
	} catch {
		return null;
	}
};

const writeStored = (language: SupportedLanguage): void => {
	try {
		window.localStorage.setItem(STORAGE_KEY, language);
	} catch {
		// Storage unavailable — the language still applies for this page load.
	}
};

const LanguageService = {
	SUPPORTED_LANGUAGES,
	DEFAULT_LANGUAGE,

	/** The language in effect: the persisted choice, else the portal default. */
	getCurrent(): SupportedLanguage {
		const stored = readStored();
		if (stored) return stored;
		// UI5 was bootstrapped with the default, so report that rather than the
		// browser locale it would otherwise have negotiated.
		const active = Localization.getLanguage();
		return isSupported(active) ? active : DEFAULT_LANGUAGE;
	},

	/** True when the value is a language the portal can render. */
	isSupported,

	/**
	 * Persist a language and reload so every already-rendered control and
	 * already-loaded plugin comes up translated. No-op when unchanged.
	 */
	apply(language: SupportedLanguage): void {
		if (language === this.getCurrent()) return;
		writeStored(language);
		window.location.reload();
	},

	/**
	 * Adopt the language stored on the user's account when it differs from this
	 * browser's — the cross-device case, e.g. first login in a new browser.
	 *
	 * Returns true when a reload was triggered; callers should stop what they
	 * are doing, since the page is being replaced.
	 */
	syncFromUser(language: unknown): boolean {
		if (!isSupported(language)) return false;
		if (language === readStored()) return false;
		writeStored(language);
		if (language === Localization.getLanguage()) return false;
		window.location.reload();
		return true;
	},
};

export default LanguageService;
